import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  isAnnouncementSeen,
  normalizeAnnouncementForInbox,
} from '../lib/announcements';

/**
 * Live announcements targeted at the signed-in user's role within their company.
 * @param {{ companyId?: string, role?: string, uid?: string } | null | undefined} userData
 * @param {{ skip?: boolean }} [options]
 */
export function useCompanyAnnouncements(userData, options = {}) {
  const skip = options.skip === true;
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(!skip);
  const uid = auth.currentUser?.uid;
  const companyId = userData?.companyId;
  const role = userData?.role;

  const enabled = Boolean(!skip && companyId && role && role !== 'super_admin');

  const refresh = useCallback(async () => {
    if (!enabled) {
      setRaw([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const q = query(
        collection(db, 'announcements'),
        where('companyId', '==', companyId),
        where('targetRoles', 'array-contains', role),
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ad = a.timestamp?.toDate?.()?.getTime() || 0;
        const bd = b.timestamp?.toDate?.()?.getTime() || 0;
        return bd - ad;
      });
      setRaw(rows);
    } catch (e) {
      console.error('useCompanyAnnouncements refresh:', e);
      setRaw([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, companyId, role]);

  useEffect(() => {
    if (!enabled) {
      setRaw([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const q = query(
      collection(db, 'announcements'),
      where('companyId', '==', companyId),
      where('targetRoles', 'array-contains', role),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const ad = a.timestamp?.toDate?.()?.getTime() || 0;
          const bd = b.timestamp?.toDate?.()?.getTime() || 0;
          return bd - ad;
        });
        setRaw(rows);
        setLoading(false);
      },
      (e) => {
        console.error('useCompanyAnnouncements listener:', e);
        setRaw([]);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [enabled, companyId, role]);

  const announcements = useMemo(
    () => raw.map((row) => normalizeAnnouncementForInbox(row)),
    [raw],
  );

  const unreadCount = useMemo(() => {
    if (!uid) return 0;
    return announcements.filter((a) => !isAnnouncementSeen(a, uid)).length;
  }, [announcements, uid]);

  return { announcements, loading, refresh, unreadCount };
}

export { isAnnouncementSeen, markAnnouncementAsSeen } from '../lib/announcements';
